import React from 'react';
import CameraTest from '../CameraTest';
import backgroundImage from '../assets/background.png';
import { useAuth } from '../hooks/useAuth';
import { useInspections } from '../hooks/useInspections';
import { useSubmission } from '../hooks/useSubmission';
import { useUI } from '../hooks/useUI';
import { useGamification } from '../hooks/useGamification';

const DashboardPage = ({
  showOnboarding,
  isTechnicianMode,
  currentPage,
  isFireExtinguisherCameraOpen,
  setIsFireExtinguisherCameraOpen,
  isQuickShotMode,
  showPyeGuide,
  setShowPyeGuide,
  showAdditionalNotes,
  setShowAdditionalNotes,
  isCustomBusinessEntry,
  setIsCustomBusinessEntry,
  showSuggestions,
  setShowSuggestions,
  locationSelectedFromPopup,
  isSubmitting,
  inspectionResult,
  isAnalyzing,
  canvasRef,
  fileInputRef,
  mainPageFileInputRef,
  isTestingCamera,
  setIsTestingCamera
}) => {
  // Extract hook values
  const { currentUser, submittedOnBehalfOf, submittedBy, setSubmittedOnBehalfOf, setSubmittedBy, getFirstNameFromEmail, validatePyeBarkerEmail, setSubmittedOnBehalfOfUser } = useAuth();
  const { inspections } = useInspections();
  const {
    selectedImage,
    setSelectedImage,
    capturedImages,
    setCapturedImages,
    location,
    setLocation,
    notes,
    setNotes,
    businessName,
    setBusinessName,
    quickShotOtherNotes,
    setQuickShotOtherNotes,
    mainPageAttachments,
    setMainPageAttachments,
    gpsData,
    gpsError,
    isCapturingGPS,
    captureGPS,
    businessSuggestions,
    isLoadingSuggestions,
    confirmationData,
    setConfirmationData,
    showConfirmationPopup,
    setShowConfirmationPopup,
    showLocationPopup,
    setShowLocationPopup,
    handleFileUpload,
    handleMainPageFileUpload,
    handleQuickShotSubmit,
    analyzeImage,
    fetchBusinessSuggestions
  } = useSubmission();
  const { showToast, playSound } = useUI();
  const { getUserTotalPoints, getPointsLeaderboard } = useGamification();

  return (
    <>
      {/* Dashboard - Quick Shot Mode */}
      {!isFireExtinguisherCameraOpen && (
        <div className="space-y-6">
          {/* 🚀 CLEAN DASHBOARD - Background */}
          <div 
            className="fixed inset-0"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              zIndex: 0
            }}
          >
            {/* OLD: Center Loading Indicator - HIDDEN, replaced by RenderProgressModal */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ display: 'none' }}>
              <div className="flex flex-col items-center space-y-4">
                {/* Loading Bar */}
                <div className="w-64 h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
                  <div className="h-full bg-pb-green rounded-full animate-pulse shadow-lg shadow-pb-green/50" style={{ width: '60%' }}></div>
                </div>
                {/* Loading Text */}
                <p className="text-white/60 text-sm backdrop-blur-sm bg-black/20 px-4 py-2 rounded-lg">Loading...</p>
              </div>
            </div>
            
            {/* Hidden Elements - Keep for fallback functionality */}
            <div style={{ display: 'none' }}>
              <img 
                src="/images/new-tagquest-green-blue-logo.png" 
                alt="TagQuest Logo"
                className="w-64 h-auto mx-auto mb-4"
              />
            </div>
            
            {/* Single Primary Action Button - Hidden but functional for fallback */}
            <div className="relative mx-auto" style={{ maxWidth: '448px', display: 'none' }}>
              <button
                onClick={() => {
                  // Open camera immediately - don't wait for GPS
                  setIsFireExtinguisherCameraOpen(true);
                  
                  // FIXED WORKFLOW: Request GPS in background (non-blocking)
                  if (!showOnboarding && !gpsData && !isCapturingGPS) {
                    console.log('🎯 Requesting GPS permission in background (non-blocking)');
                    captureGPS(); // Run in background, don't await
                  }
                }}
                className="group relative w-full rounded-2xl overflow-hidden transition-all duration-300 shadow-xl focus:outline-none"
                aria-label="Click to scan fire extinguisher safety tag"
                title="Click to scan fire extinguisher safety tag"
                style={{ aspectRatio: '1 / 1' }}
              >
                {/* Video replaces static image */}
                <video
                  src="/video/Extinguisher%20tag%20camera%20video.m4v"
                  autoPlay
                  loop
                  playsInline
                  muted
                  preload="auto"
                  className="w-full h-full object-cover pointer-events-none select-none transition-all duration-300 group-hover:scale-105"
                  onContextMenu={(e) => e.preventDefault()}
                  onError={(e) => {
                    // Silently handle video loading errors - not critical for functionality
                    console.log('Video not available - using fallback');
                    e.target.style.display = 'none';
                  }}
                  draggable="false"
                />
                {/* Animated border - rotating gold/red gradient */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden">
                  <div className="absolute inset-0 rounded-2xl border-4 border-transparent animate-border-flow" 
                       style={{
                         background: 'linear-gradient(90deg, #FFD700, #FF6B35, #FFD700, #FF6B35) border-box',
                         WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                         WebkitMaskComposite: 'xor',
                         maskComposite: 'exclude',
                         animation: 'borderFlow 3s linear infinite'
                       }}>
                  </div>
                </div>
                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-2xl ring-1 ring-orange-400/20 pointer-events-none group-hover:ring-orange-400/40 group-hover:shadow-[0_0_0_4px_rgba(251,146,60,0.15)]"></div>
              </button>
            </div>

            {/***
             ### ORIGINAL QUICK SHOT BUTTON (blocked for redesign)
             <button
               onClick={() => {
                 setIsFireExtinguisherCameraOpen(true);
               }}
               className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-6 px-8 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-xl text-xl backdrop-blur-md border border-orange-400/30"
             >
               📷 Capture Fire Extinguisher
             </button>
             ### END ORIGINAL QUICK SHOT BUTTON
            ***/}
            
            <p className="text-white/60 text-sm mt-4" style={{ display: 'none' }}>
              One-tap photo capture for AI analysis of inspection dates{' '}
              {/* ### STEALTH UPLOAD FOR TESTING ### */}
              <span 
                  onClick={() => fileInputRef.current?.click()}
                className="text-white/20 hover:text-white/40 cursor-pointer transition-all duration-300"
                title="Upload File (Testing)"
              >
                .
              </span>
            </p>
            
            {/* ### MVP: GALLERY UPLOAD COMPLETELY HIDDEN FOR END USERS ### */}
            {/* ### ADMIN ACCESS: Hidden file input maintained for testing ### */}
            
            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, playSound, showToast)}
              className="hidden"
            />

            {/* Hidden Main Page Attachments Input */}
            <input
              ref={mainPageFileInputRef}
              type="file"
              accept="*/*"
              multiple
              onChange={(e) => handleMainPageFileUpload(e)}
              className="hidden"
            />



            {/* Display Selected Image - HIDDEN FOR DEMO (image only shows in confirmation popup) */}
            {false && selectedImage && (
              <div className="mt-4">
                <img src={selectedImage} alt="Selected" className="max-w-full h-64 object-cover rounded-lg border border-white/20" />
              </div>
            )}

            {/* Display Main Page Attachments */}
            {mainPageAttachments.length > 0 && (
              <div className="mt-4">
                <h4 className="text-white text-sm font-medium mb-2">📎 Attached Files ({mainPageAttachments.length})</h4>
                <div className="space-y-2">
                  {mainPageAttachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white/10 rounded-lg p-2 border border-white/20">
                      <span className="text-white text-xs truncate flex-1">{file.name}</span>
                      <button
                        onClick={() => setMainPageAttachments(prev => prev.filter((_, i) => i !== index))}
                        className="ml-2 text-red-400 hover:text-red-300 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pye Guide - First Time Helper - Top Right with X - HIDDEN FOR DEMO */}
          {false && showPyeGuide && (
            <div className="fixed right-8 top-24 z-40">
              {/* Close button - always visible */}
              <button
                onClick={() => {
                  setShowPyeGuide(false);
                  localStorage.setItem('pyeGuideCompleted', 'true');
                }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full text-white flex items-center justify-center text-xs font-bold z-10 shadow-lg"
              >
                ✕
              </button>
              
              {/* Step 1: Take a picture first! */}
              {!selectedImage && (
                <div className="flex items-center gap-3">
                  <div className="bg-orange-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg animate-pulse">
                    ← Step 1: Tap the tag to scan!
                  </div>
                  <img 
                    src="/images/new-tagquest-green-blue-logo.png" 
                    alt="Pye Guide"
                    className="w-24 h-auto animate-bounce"
                  />
                </div>
              )}
              
              {/* Step 2: Enter business name */}
              {selectedImage && !businessName && (
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg animate-pulse">
                    ← Step 2: Enter business name below
                  </div>
                  <img 
                    src="/images/new-tagquest-green-blue-logo.png" 
                    alt="Pye Guide"
                    className="w-24 h-auto animate-bounce"
                  />
                </div>
              )}
              
              {/* Step 3: Submit! */}
              {selectedImage && businessName && (
                <div className="flex items-center gap-3">
                  <div className="bg-green-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg animate-pulse">
                    ← Step 3: Hit Submit & Go!
                  </div>
                  <img 
                    src="/images/new-tagquest-green-blue-logo.png" 
                    alt="Pye Guide"
                    className="w-24 h-auto animate-bounce"
                  />
                </div>
              )}
            </div>
          )}

          {/* Quick Shot Form */}
          <div className="bg-white/8 backdrop-blur-md rounded-xl p-6 border border-white/20 relative">
            <div className="space-y-6">
              {/* Submitted By Display - HIDDEN FOR DEMO */}
              {false && (
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  👤 Submitted By (PBFS Email Only)
                </label>
                <div className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white">
                  {getFirstNameFromEmail(submittedOnBehalfOf || currentUser)}
                </div>
              </div>
              )}

              {/* Business Name Field - HIDDEN FOR DEMO (Paperclip also hidden) */}
              {false && (
              <div>
                <div className="flex items-center justify-end mb-3">
                  <button
                    type="button"
                    onClick={() => setShowAdditionalNotes(!showAdditionalNotes)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-all duration-300 text-white/70 hover:text-white"
                    title="Additional Notes (Optional)"
                  >
                    📎
                  </button>
                </div>
                {false && (<div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter business name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    onFocus={() => {
                      if (!isCustomBusinessEntry && !locationSelectedFromPopup) {
                        setShowSuggestions(businessSuggestions.length > 0);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isCustomBusinessEntry) {
                        setShowSuggestions(false);
                      }
                    }}
                    className="w-full px-4 py-4 text-lg bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  
                  {/* Custom Business Entry Indicator */}
                  {isCustomBusinessEntry && (
                    <div className="mt-2 p-2 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-blue-400">
                          <span>✏️</span>
                          <span className="text-sm font-medium">Custom Business Entry Mode</span>
                        </div>
                        <button
                          onClick={() => {
                            setIsCustomBusinessEntry(false);
                            setBusinessName('');
                          }}
                          className="text-blue-300 hover:text-blue-200 text-xs underline"
                        >
                          Back to suggestions
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Business Suggestions Dropdown */}
                  {showSuggestions && !locationSelectedFromPopup && (businessSuggestions.length > 0 || isLoadingSuggestions) && (
                    <div className="absolute z-10 w-full mt-2 bg-black/90 backdrop-blur-md rounded-lg border border-white/20 max-h-64 overflow-y-auto">
                      {isLoadingSuggestions ? (
                        <div className="p-4 text-center text-white/70">
                          🔍 Finding nearby businesses...
                        </div>
                      ) : (
                        <>
                          {/* Add Custom Business Option - TOP */}
                          <div className="p-3 border-b border-white/20">
                            <button
                              onClick={() => {
                                setIsCustomBusinessEntry(true);
                                setShowSuggestions(false);
                                setBusinessName('');
                              }}
                              className="w-full text-left p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors duration-300"
                            >
                              <div className="flex items-center space-x-2">
                                <span>➕</span>
                                <span className="font-medium">Add Custom Business</span>
                              </div>
                              <div className="text-xs text-blue-300 mt-1">Enter business name manually</div>
                            </button>
                          </div>
                          
                          {/* GPS Suggestions Header */}
                          <div className="p-3 border-b border-white/20">
                            <div className="text-sm text-white/70 mb-2">📍 Nearby businesses based on GPS:</div>
                          </div>
                          
                          {/* Business Suggestions */}
                          {businessSuggestions.map((business, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                // DEMO: Show confirmation popup instead of immediate submission
                                setBusinessName(business.name);
                                setShowSuggestions(false);
                                setConfirmationData({
                                  image: selectedImage,
                                  address: business.name,
                                  fullBusiness: business
                                });
                                setShowConfirmationPopup(true);
                              }}
                              className="w-full text-left p-3 hover:bg-white/10 transition-colors duration-300 border-b border-white/10 last:border-b-0"
                            >
                              <div className="text-white font-medium">{business.name}</div>
                              {business.address && (
                                <div className="text-white/60 text-sm">{business.address}</div>
                              )}
                              {business.phone && (
                                <div className="text-green-400 text-sm">📱 {business.phone}</div>
                              )}
                              {business.rating && (
                                <div className="text-yellow-400 text-sm">⭐ {business.rating}</div>
                              )}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
                </div>
                )}
              </div>
              )}

              {/* ### GPS Capture Section - COMMENTED OUT FOR CLEANER UI (GPS is automated) ### */}
              {/* ### UNCOMMENT BELOW TO REACTIVATE MANUAL GPS CAPTURE ### */}
              {/*
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  📍 GPS Location
                </label>
                <div className="space-y-3">
                  {gpsData ? (
                    <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-center space-x-2 text-green-400">
                        <span>✅</span>
                        <span className="font-medium">GPS Captured Successfully</span>
                      </div>
                      <div className="text-white/70 text-sm mt-1">
                        {gpsData.latitude.toFixed(6)}, {gpsData.longitude.toFixed(6)} (±{gpsData.accuracy}m)
                      </div>
                      {gpsError && (
                        <div className="text-orange-400 text-sm mt-2">
                          ⚠️ {gpsError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                      <div className="flex items-center justify-between">
                        <div className="text-white/70">
                          {isCapturingGPS ? 'Capturing GPS...' : 'No GPS data captured'}
                        </div>
                        <button
                          onClick={captureGPS}
                          disabled={isCapturingGPS}
                          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-lg transition-colors duration-300"
                        >
                          {isCapturingGPS ? '⏳ Capturing...' : '📍 Capture GPS'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              */}



              {/* Additional Notes Section - Hidden by default, toggled by paperclip icon */}
              {false && showAdditionalNotes && (
                <div className="animate-slideIn">
                  <label className="block text-white text-sm font-medium mb-2">
                    📝 Additional Notes (Optional)
                  </label>
                  <div className="relative">
                    <textarea
                      placeholder="Add any additional notes or observations..."
                      value={quickShotOtherNotes}
                      onChange={(e) => setQuickShotOtherNotes(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                    />
                    <div className="absolute bottom-3 right-3 flex space-x-2">
                      {/* BLOCKED OUT - Voice Recording Button (can restore later)
                      <button
                        onClick={() => setIsVoiceNotesOpen(true)}
                        className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors duration-300"
                        title="Record voice note"
                      >
                        🎤
                      </button>
                      */}
                      {/* File Attachment Button */}
                      {/* REMOVED: Paperclip UI per Alex feedback - use multi-image scan instead */}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button - HIDDEN FOR DEMO (moved to confirmation popup) */}
              {false && (
              <div className="pt-4">
                <button
                  onClick={handleQuickShotSubmit}
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-500 disabled:to-gray-600 text-white font-bold text-lg rounded-lg transition-all duration-300 transform hover:scale-105 disabled:transform-none rotating-glow"
                >
                  {isSubmitting ? '⏳ Submitting...' : '🚀 Submit & Go!'}
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Technician Mode Dashboard - REMOVED FOR NOW, USING MAIN DASHBOARD ONLY */}
      {false && isQuickShotMode && (
        <div className="space-y-8">
          {isQuickShotMode ? (
            /* Quick Shot Mode Interface */
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md rounded-xl p-6 border border-green-500/50 green-glow">
                <div className="flex items-center space-x-3 mb-4">
                  <span className="text-2xl">⚡</span>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Quick Shot Mode</h2>
                    <p className="text-white/70">Snap → Name → Submit → Go!</p>
                  </div>
                </div>
              </div>

              {/* Quick Shot Form */}
              <div className="bg-white/8 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <div className="space-y-6">
                  {/* Submitted By Field - HIDDEN FOR DEMO */}
                  {false && (
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      📧 Submitted On Behalf Of (PyeBarkerFS Email)
                    </label>
                    <input
                      type="email"
                      placeholder="your.email@pyebarkerfs.com"
                      value={submittedOnBehalfOf || submittedBy}
                      onChange={(e) => {
                        setSubmittedOnBehalfOf(e.target.value);
                        setSubmittedBy(e.target.value.split('@')[0].replace('.', ' '));
                      }}
                      onBlur={(e) => {
                        const email = e.target.value.trim();
                        if (email && validatePyeBarkerEmail(email)) {
                          setSubmittedOnBehalfOfUser(email);
                        }
                      }}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  )}

                  {/* Business Name Field - HIDDEN FOR DEMO (Paperclip also hidden) */}
                  {false && (
                  <div>
                    <div className="flex items-center justify-end mb-3">
                      <button
                        type="button"
                        onClick={() => setShowAdditionalNotes(!showAdditionalNotes)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-all duration-300 text-white/70 hover:text-white"
                        title="Additional Notes (Optional)"
                      >
                        📎
                      </button>
                    </div>
                    {false && (<div className="relative">
                      <input
                        type="text"
                        placeholder="Enter business name"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        onFocus={() => {
                          if (!isCustomBusinessEntry) {
                            setShowSuggestions(businessSuggestions.length > 0);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && isCustomBusinessEntry) {
                            setShowSuggestions(false);
                          }
                        }}
                        className="w-full px-4 py-4 text-lg bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      
                      {/* Custom Business Entry Indicator */}
                      {isCustomBusinessEntry && (
                        <div className="mt-2 p-2 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-blue-400">
                              <span>✏️</span>
                              <span className="text-sm font-medium">Custom Business Entry Mode</span>
                            </div>
                            <button
                              onClick={() => {
                                setIsCustomBusinessEntry(false);
                                setBusinessName('');
                              }}
                              className="text-blue-300 hover:text-blue-200 text-xs underline"
                            >
                              Back to suggestions
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Business Suggestions Dropdown */}
                      {showSuggestions && (businessSuggestions.length > 0 || isLoadingSuggestions) && (
                        <div className="absolute z-10 w-full mt-2 bg-black/90 backdrop-blur-md rounded-lg border border-white/20 max-h-64 overflow-y-auto">
                          {isLoadingSuggestions ? (
                            <div className="p-4 text-center text-white/70">
                              🔍 Finding nearby businesses...
                            </div>
                          ) : (
                            <>
                              {/* Add Custom Business Option - TOP */}
                              <div className="p-3 border-b border-white/20">
                                <button
                                  onClick={() => {
                                    setIsCustomBusinessEntry(true);
                                    setShowSuggestions(false);
                                    setBusinessName('');
                                  }}
                                  className="w-full text-left p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors duration-300"
                                >
                                  <div className="flex items-center space-x-2">
                                    <span>➕</span>
                                    <span className="font-medium">Add Custom Business</span>
                                  </div>
                                  <div className="text-xs text-blue-300 mt-1">Enter business name manually</div>
                                </button>
                              </div>
                              
                              {/* GPS Suggestions Header */}
                              <div className="p-3 border-b border-white/20">
                                <div className="text-sm text-white/70 mb-2">📍 Nearby businesses based on GPS:</div>
                              </div>
                              
                              {/* Business Suggestions */}
                              {businessSuggestions.map((business, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    // DEMO: Show confirmation popup instead of immediate submission
                                    setBusinessName(business.name);
                                    setShowSuggestions(false);
                                    setConfirmationData({
                                      image: selectedImage,
                                      address: business.name,
                                      fullBusiness: business
                                    });
                                    setShowConfirmationPopup(true);
                                  }}
                                  className="w-full text-left p-3 hover:bg-white/10 transition-colors duration-300 border-b border-white/10 last:border-b-0"
                                >
                                  <div className="text-white font-medium">{business.name}</div>
                                  {business.address && (
                                    <div className="text-white/60 text-sm">{business.address}</div>
                                  )}
                                  {business.rating && (
                                    <div className="text-yellow-400 text-sm">⭐ {business.rating}</div>
                                  )}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                  )}

                  {/* ### GPS Capture Section - COMMENTED OUT FOR CLEANER UI (GPS is automated) ### */}
                  {/* ### UNCOMMENT BELOW TO REACTIVATE MANUAL GPS CAPTURE ### */}
                  {/*
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      📍 GPS Location
                    </label>
                    <div className="space-y-3">
                      {gpsData ? (
                        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                          <div className="flex items-center space-x-2 text-green-400">
                            <span>✅</span>
                            <span className="font-medium">GPS Captured Successfully</span>
                          </div>
                          <div className="text-white/70 text-sm mt-1">
                            {gpsData.latitude.toFixed(6)}, {gpsData.longitude.toFixed(6)} (±{gpsData.accuracy}m)
                          </div>
                          {gpsError && (
                            <div className="text-orange-400 text-sm mt-2">
                              ⚠️ {gpsError}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                          <div className="flex items-center justify-between">
                            <div className="text-white/70">
                              {isCapturingGPS ? 'Capturing GPS...' : 'No GPS data captured'}
                            </div>
                            <button
                              onClick={captureGPS}
                              disabled={isCapturingGPS}
                              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-lg transition-colors duration-300"
                            >
                              {isCapturingGPS ? '⏳ Capturing...' : '📍 Capture GPS'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  */}

                  {/* Additional Notes Section - Hidden by default, toggled by paperclip icon */}
                  {false && showAdditionalNotes && (
                    <div className="animate-slideIn">
                      <label className="block text-white text-sm font-medium mb-2">
                        📝 Additional Notes (Optional)
                      </label>
                      <div className="relative">
                        <textarea
                          placeholder="Add any additional notes or observations..."
                          value={quickShotOtherNotes}
                          onChange={(e) => setQuickShotOtherNotes(e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                        />
                        <div className="absolute bottom-3 right-3 flex space-x-2">
                          {/* BLOCKED OUT - Voice Recording Button (can restore later)
                          <button
                            onClick={() => setIsVoiceNotesOpen(true)}
                            className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors duration-300"
                            title="Record voice note"
                          >
                            🎤
                          </button>
                          */}
                          {/* File Attachment Button */}
                          <button
                            onClick={() => mainPageFileInputRef.current?.click()}
                            className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors duration-300 hidden"
                            title="Attach files (hidden for pilot)"
                          >
                            📎
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      onClick={handleQuickShotSubmit}
                      disabled={isSubmitting}
                      className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-500 disabled:to-gray-600 text-white font-bold text-lg rounded-lg transition-all duration-300 transform hover:scale-105 disabled:transform-none rotating-glow"
                    >
                      {isSubmitting ? '⏳ Submitting...' : '🚀 Submit & Go!'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Technician Mode Interface */
            <div className="space-y-8">
              {/* Image Upload Section */}
              <div className="bg-white/8 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <h2 className="text-2xl font-bold text-white mb-6">Upload Fire Extinguisher Tag</h2>
                
                <div className="space-y-4">
                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        fileInputRef.current.click();
                        // Auto-capture GPS when selecting file for accurate location
                        if (!gpsData && !isCapturingGPS) {
                          captureGPS();
                        }
                      }}
                      className="flex-1 bg-gradient-to-r from-gray-700 to-gray-800 text-white font-bold py-3 px-6 rounded-lg hover:from-gray-600 hover:to-gray-700 transition-all duration-300 transform hover:scale-105 backdrop-blur-md border border-white/20"
                    >
                      📁 Choose File
                    </button>
                    <button
                      onClick={() => {
                        // Open camera immediately (non-blocking)
                        setIsFireExtinguisherCameraOpen(true);
                        
                        // Request GPS in background if needed (non-blocking)
                        if (!gpsData && !isCapturingGPS) {
                          console.log('🎯 Requesting GPS in background (non-blocking)');
                          captureGPS(); // Run in background, don't await
                        }
                      }}
                      className="flex-1 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-3 px-6 rounded-lg hover:from-gray-500 hover:to-gray-600 transition-all duration-300 transform hover:scale-105 backdrop-blur-md border border-white/20"
                    >
                      {capturedImages.length === 0 
                        ? '📷 Take Photo' 
                        : `📷 Add Image (${capturedImages.length + 1})`
                      }
                    </button>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />



                  {/* Camera Test Modal (for debugging) */}
                  {isTestingCamera && <CameraTest onExit={() => setIsTestingCamera(false)} />}
                  
                  <canvas ref={canvasRef} className="hidden" />

                  {/* PILOT: Multi-image gallery display */}
                  {capturedImages.length > 0 && (
                    <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-white font-semibold">📸 Captured Images ({capturedImages.length})</h4>
                        <button
                          onClick={() => {
                            setCapturedImages([]);
                            setSelectedImage(null);
                            showToast('🗑️ Cleared all images', 'info');
                          }}
                          className="text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded"
                        >
                          Clear All
                        </button>
                      </div>
                      
                      <div className="flex gap-2 overflow-x-auto">
                        {capturedImages.map((image, index) => (
                          <div key={image.id} className="relative group flex-shrink-0">
                            <img
                              src={image.data}
                              alt={`Captured image ${index + 1}`}
                              className="w-24 h-24 object-cover rounded border border-white/20 group-hover:border-white/40 transition-colors cursor-pointer"
                              onClick={() => setSelectedImage(image.data)} // For preview
                            />
                            <button
                              onClick={() => {
                                const updatedImages = capturedImages.filter(img => img.id !== image.id);
                                setCapturedImages(updatedImages);
                                // If removing primary image, make next one primary
                                if (index === 0 && updatedImages.length > 0) {
                                  setSelectedImage(updatedImages[0].data);
                                  updatedImages[0].type = 'primary';
                                } else if (updatedImages.length === 0) {
                                  setSelectedImage(null);
                                }
                                showToast(`🗑️ Removed image ${index + 1}`, 'info');
                              }}
                              className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 rounded-b text-center">
                              {index === 0 ? '🎯 Main' : `+${index}`}
                            </div>
                            {image.type === 'primary' && (
                              <div className="absolute top-0 left-0 bg-green-500 text-white text-xs px-1 py-0.5 rounded-br">
                                AI
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-white/60 text-xs mt-2 text-center">
                        💡 Tap "Add Image" to capture more tags • Click image for preview
                      </p>
                    </div>
                  )}

                  {/* Legacy single image display (fallback) - HIDDEN FOR DEMO */}
                  {false && selectedImage && capturedImages.length === 0 && (
                    <div className="mt-4">
                      <img src={selectedImage} alt="Selected" className="max-w-full h-64 object-cover rounded-lg border border-white/20" />
                    </div>
                  )}

                  {/* GPS Section */}
                  <div className="bg-black/20 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold">📍 GPS Location</h3>
                      <button
                        onClick={captureGPS}
                        disabled={isCapturingGPS}
                        className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded hover:bg-blue-500/30 transition-colors duration-300 disabled:opacity-50"
                      >
                        {isCapturingGPS ? '📡 Getting GPS...' : '📡 Capture GPS'}
                      </button>
                    </div>
                    
                    {gpsData && (
                      <div className="text-sm text-green-400">
                        ✅ GPS: {gpsData.latitude.toFixed(6)}, {gpsData.longitude.toFixed(6)} 
                        <span className="text-white/60 ml-2">(±{Math.round(gpsData.accuracy)}m)</span>
                      </div>
                    )}
                    
                    {gpsError && (
                      <div className="text-sm text-red-400">
                        ❌ GPS Error: {gpsError}
                      </div>
                    )}
                    
                    {!gpsData && !gpsError && !isCapturingGPS && (
                      <div className="text-sm text-white/60">
                        Click "Capture GPS" to get location automatically
                      </div>
                    )}
                  </div>

                  {/* Location and Notes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        📍 Location
                      </label>
                      <input
                        type="text"
                        placeholder="Enter location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        📝 Notes
                      </label>
                      <textarea
                        placeholder="Add notes (optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Analyze Button */}
                  <button
                    onClick={analyzeImage}
                    disabled={!selectedImage || isAnalyzing}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold py-4 px-6 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
                  >
                    {isAnalyzing ? '🔍 Analyzing...' : '🔍 Analyze Image'}
                  </button>
                </div>
              </div>

              {/* Analysis Results */}
              {inspectionResult && (
                <div className="bg-white/8 backdrop-blur-md rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">Analysis Results</h3>
                  <div className="bg-black/20 rounded-lg p-4">
                    <pre className="text-white text-sm whitespace-pre-wrap">{JSON.stringify(inspectionResult, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DashboardPage;

